<template>
    <div class="widget-component">
        <div class="epc-chart my-2">
            <div class="epc-title" v-html="title"></div>
            <div class="bars">
                <div class="bar">
                    <div :class="firstColClass"><span class="th"></span></div>
                    <div class="col_2">
                        <span class="th">{{
                            currentOnly ? "Predicted" : "Current"
                        }}</span>
                    </div>
                    <div class="col_3" v-if="!currentOnly">
                        <span class="th">Potential</span>
                    </div>
                </div>
            </div>
            <div class="bars noborder_bottom">
                <div class="bar topbottomtext">
                    <em v-html="topText"></em>
                </div>
            </div>
            <div class="bars noborder_bottom">
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_a', kind]">
                            <span class="range">(92-100)</span>
                            <span class="title">A</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_b', kind]">
                            <span class="range">(81-91)</span>
                            <span class="title">B</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_c', kind]">
                            <span class="range">(69-80)</span>
                            <span class="title">C</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_d', kind]">
                            <span class="range">(55-68)</span>
                            <span class="title">D</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_e', kind]">
                            <span class="range">(39-54)</span>
                            <span class="title">E</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_f', kind]">
                            <span class="range">(21-38)</span>
                            <span class="title">F</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div class="bar">
                    <div :class="firstColClass">
                        <span :class="['eff-rating', 'rating_g', kind]">
                            <span class="range">(1-20)</span>
                            <span class="title">G</span>
                        </span>
                    </div>
                    <div class="col_2"></div>
                    <div class="col_3" v-if="!currentOnly"></div>
                </div>
                <div
                    :class="[
                        'pointer',
                        currentOnly ? 'current_only' : 'current',
                        'rating_' + currentBand,
                        kind,
                    ]"
                    :style="{ top: currentTop + 'px' }"
                >
                    <span :class="{ smaller: current == 100 }">{{ current }}</span>
                </div>
                <div
                    v-if="!currentOnly"
                    :class="[
                        'pointer',
                        'potential',
                        'rating_' + potentialBand,
                        kind,
                    ]"
                    :style="{ top: potentialTop + 'px' }"
                >
                    <span :class="{ smaller: current == 100 }">{{
                        potential
                    }}</span>
                </div>
            </div>
            <div class="bars noborder_top">
                <div class="bar topbottomtext">
                    <em v-html="bottomText"></em>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent } from "@vue/composition-api";
    export default defineComponent({
        name: "EPCChart",
        props: {
            kind: {
                type: String,
                default: "energy"
            },
            current: {
                type: Number,
                required: true,
            },
            potential: {
                type: Number,
                required: false,
            },
        },
        setup(props) {
            const currentOnly = computed(() => props.potential == null);
            const title = computed(() =>
                props.kind == "energy"
                    ? "Energy Efficiency Rating"
                    : "Environmental Impact (CO<sub>2</sub>) Rating"
            );
            const topText = computed(() =>
                props.kind == "energy"
                    ? "Very energy efficient - lower running costs"
                    : "Very environmental friendly - lower CO<sub>2</sub> emissions"
            );
            const bottomText = computed(() =>
                props.kind == "energy"
                    ? "Not energy efficient - higher running costs"
                    : "Not environmental friendly - higher CO<sub>2</sub> emissions"
            );
            const firstColClass = computed(() =>
                props.potential == null ? "col_0" : "col_1"
            );
            const currentBand = computed(() =>
                props.current <= 20
                    ? "g"
                    : props.current <= 38
                    ? "f"
                    : props.current <= 54
                    ? "e"
                    : props.current <= 68
                    ? "d"
                    : props.current <= 80
                    ? "c"
                    : props.current <= 91
                    ? "b"
                    : "a"
            );
            const currentTop = computed(
                () => (currentBand.value.charCodeAt(0) - 97) * 38
            );
            const potentialBand = computed(() =>
                !props.potential || props.potential <= 20
                    ? "g"
                    : props.potential <= 38
                    ? "f"
                    : props.potential <= 54
                    ? "e"
                    : props.potential <= 68
                    ? "d"
                    : props.potential <= 80
                    ? "c"
                    : props.potential <= 91
                    ? "b"
                    : "a"
            );
            const potentialTop = computed(
                () => (potentialBand.value.charCodeAt(0) - 97) * 38
            );
            return {
                currentOnly,
                title,
                topText,
                bottomText,
                firstColClass,
                currentBand,
                currentTop,
                potentialBand,
                potentialTop,
            };
        },
    });
</script>

<style lang="scss">
    .epc-chart {
        width: 100%;
        background-color: white;
        span {
            line-height: normal !important;
            -webkit-font-smoothing: auto !important;
        }
        .topbottomtext {
            color: black;
            font-size: 14px;
            line-height: 1em;
            em {
                display: block;
                padding: 8px 4px;
            }
        }
        .epc-title {
            color: white;
            background-color: #266db5;
            font-size: 1.1em;
            font-weight: 600;
            width: 100%;
            padding: 2px 5px;
            margin-bottom: 2px;
        }
        .bars {
            border: 1px solid #0b0c0c;
            display: table;
            position: relative;
            width: 100%;
            &.noborder_bottom {
                border-bottom: 0;
            }
            &.noborder_bottom {
                border-top: 0;
            }
            .bar {
                display: table-row;
                .th {
                    font-size: 14px;
                    padding: 5px;
                    color: black;
                }
                div {
                    display: table-cell;
                }
                .col_0 {
                    width: 80%;
                }
                .col_1 {
                    width: 60%;
                }
                .col_2,
                .col_3 {
                    width: 20%;
                }
                .eff-rating {
                    display: block;
                    font-size: 12px;
                    margin-bottom: 2px;
                    overflow: auto;
                    padding: 0 4px;
                    .range {
                        position: relative;
                        top: 10px;
                        font-size: 14px;
                        padding-left: 8px;
                    }
                    .title {
                        color: #fff;
                        float: right;
                        font-size: 24px;
                        line-height: 36px !important;
                        font-weight: 700;
                        text-align: right;
                        text-shadow: -1px 0 black, 0 1px black, 1px 0 black,
                            0 -1px black;
                    }
                }
            }
            .rating_a_plus {
                margin-right: 60%;
                &.energy {
                    background: #008054;
                    color: white;
                }
                &.co2 {
                    background: #cde2f5;
                    color: black;
                }
            }
            .rating_a {
                margin-right: 52%;
                &.energy {
                    background: #008054;
                    color: white;
                }
                &.co2 {
                    background: #cde2f5;
                    color: black;
                }
            }
            .rating_b {
                margin-right: 44%;
                &.energy {
                    background: #19b459;
                    color: white;
                }
                &.co2 {
                    background: #97c0ef;
                    color: black;
                }
            }
            .rating_c {
                margin-right: 36%;
                &.energy {
                    background: #8dce46;
                    color: white;
                }
                &.co2 {
                    background: #73a2d6;
                    color: black;
                }
            }
            .rating_d {
                margin-right: 28%;
                &.energy {
                    background: #ffd500;
                    color: white;
                }
                &.co2 {
                    background: #4e84c4;
                    color: black;
                }
            }
            .rating_e {
                margin-right: 20%;
                &.energy {
                    background: #fcaa65;
                    color: white;
                }
                &.co2 {
                    background: #a8a8a8;
                    color: black;
                }
            }
            .rating_f {
                margin-right: 12%;
                color: white;
                &.energy {
                    background: #ef8023;
                }
                &.co2 {
                    background: #868686;
                }
            }
            .rating_g {
                margin-right: 4%;
                color: white;
                &.energy {
                    background: #e9153b;
                }
                &.co2 {
                    background: #686868;
                }
            }
            small {
                display: block;
                line-height: 1em;
                padding: 8px 4px;
            }
            .pointer {
                display: block;
                margin: 0;
                position: absolute;
                font-size: 22px;
                &.current_only {
                    left: 86%;
                }
                &.current {
                    left: 66%;
                }
                &.potential {
                    left: 86%;
                }
                span {
                    color: #fff;
                    font-size: 22px;
                    line-height: 36px !important;
                    font-weight: 500;
                    padding: 2px 3px;
                    position: relative;
                    text-shadow: -1px 0 black, 0 1px black, 1px 0 black,
                        0 -1px black;
                    &:after {
                        border: solid transparent;
                        border-width: 18px;
                        content: " ";
                        height: 0;
                        margin-top: -18px;
                        pointer-events: none;
                        position: absolute;
                        right: 100%;
                        top: 50%;
                        width: 0;
                    }
                    &.smaller {
                        font-size: 20px !important;
                    }

                    @media only screen and (max-width: 400px) {
                        font-size: 16px;
                    }
                }
            }
            .rating_a.energy span:after {
                border-right-color: #008054;
            }
            .rating_a.co2 span:after {
                border-right-color: #cde2f5;
            }
            .rating_b.energy span:after {
                border-right-color: #19b459;
            }
            .rating_b.co2 span:after {
                border-right-color: #97c0ef;
            }
            .rating_c.energy span:after {
                border-right-color: #8dce46;
            }
            .rating_c.co2 span:after {
                border-right-color: #73a2d6;
            }
            .rating_d.energy span:after {
                border-right-color: #ffd500;
            }
            .rating_d.co2 span:after {
                border-right-color: #4e84c4;
            }
            .rating_e.energy span:after {
                border-right-color: #fcaa65;
            }
            .rating_e.co2 span:after {
                border-right-color: #a8a8a8;
            }
            .rating_f.energy span:after {
                border-right-color: #ef8023;
            }
            .rating_f.co2 span:after {
                border-right-color: #868686;
            }
            .rating_g.energy span:after {
                border-right-color: #e9153b;
            }
            .rating_g.co2 span:after {
                border-right-color: #686868;
            }
        }
        .bars + .bars {
            border-top: 0;
        }
    }
</style>